/* ============================================================================
   Canasta Básica · Monitor BAMX — Sistema de diseño
   ----------------------------------------------------------------------------
   Tokens. Nada de hex sueltos fuera de este archivo.

   CLARO Y OSCURO EN UN SOLO LUGAR.
   Cada token de color se declara una vez con `light-dark(claro, oscuro)`. La
   versión previa repetía el bloque oscuro dos veces —una en la media query del
   sistema y otra en `[data-theme="dark"]` para el toggle— y los dos se
   desincronizaron: el tema oscuro manual se quedó con los deltas rojo/verde y
   con una tinta atenuada que no cumplía contraste, o sea que las correcciones de
   accesibilidad no llegaban a quien elegía oscuro a mano. Con `light-dark()` esa
   clase de error es imposible: sólo existe un valor por token y por modo.

   `color-scheme` es el interruptor. `light dark` deja decidir al sistema; el
   toggle del usuario lo fija y gana en los dos sentidos.

   Cada token de color se declara DOS VECES a propósito: primero el valor claro
   solo y luego el `light-dark()`. Un navegador sin soporte descarta la segunda
   declaración y se queda en modo claro, que es un degradado aceptable; con
   soporte, la segunda gana.

   Tipografía: pila del sistema (San Francisco en Apple), con tracking y leading
   específicos por tamaño — nunca un letter-spacing único para toda la escala.
   Color de datos: paleta categórica documentada, validada con
   `npm run validate:palette` (PASS en claro y oscuro; el reporte incluye un
   guard que impide usar los tokens de dirección como color de texto).
   ========================================================================== */

:root {
  color-scheme: light dark;

  /* --- Tipografía --------------------------------------------------------- */
  --font-sans: system-ui, -apple-system, 'SF Pro Text', 'Segoe UI', Roboto,
    'Helvetica Neue', sans-serif;
  --font-mono: ui-monospace, 'SF Mono', SFMono-Regular, Menlo, monospace;

  /* El tracking es específico por tamaño: se aprieta al crecer y se suelta al
     encoger. Moderado a propósito — la pila del sistema resuelve a SF, que ya
     trae tracking óptico; apretar -0.03em encima lo aprieta dos veces. */
  --t-hero-size: clamp(2.5rem, 1.6rem + 3.4vw, 4rem);
  --t-hero-lh: 1.02;
  --t-hero-ls: -0.014em;

  --t-title1-size: 1.75rem;
  --t-title1-lh: 1.16;
  --t-title1-ls: -0.016em;

  --t-title2-size: 1.3125rem;
  --t-title2-lh: 1.22;
  --t-title2-ls: -0.013em;

  --t-title3-size: 1.0625rem;
  --t-title3-lh: 1.3;
  --t-title3-ls: -0.009em;

  --t-body-size: 0.9375rem;
  --t-body-lh: 1.55;
  --t-body-ls: -0.002em;

  --t-callout-size: 0.875rem;
  --t-callout-lh: 1.5;
  --t-callout-ls: 0;

  --t-footnote-size: 0.8125rem;
  --t-footnote-lh: 1.46;
  --t-footnote-ls: 0.004em;

  --t-caption-size: 0.75rem;
  --t-caption-lh: 1.4;
  --t-caption-ls: 0.01em;

  --t-micro-size: 0.6875rem;
  --t-micro-lh: 1.3;
  --t-micro-ls: 0.055em;

  --w-regular: 400;
  --w-medium: 500;
  --w-semibold: 600;
  --w-bold: 700;

  /* --- Espaciado (escala de 4px, en rem para escalar con el texto) -------- */
  --s-05: 0.125rem; /*  2 */
  --s-1: 0.25rem; /*  4 */
  --s-2: 0.5rem; /*  8 */
  --s-3: 0.75rem; /* 12 */
  --s-4: 1rem; /* 16 */
  --s-5: 1.25rem; /* 20 */
  --s-6: 1.5rem; /* 24 */
  --s-8: 2rem; /* 32 */
  --s-10: 2.5rem; /* 40 */
  --s-12: 3rem; /* 48 */
  --s-16: 4rem; /* 64 */
  --s-20: 5rem; /* 80 */
  --s-24: 6rem; /* 96 */

  /* --- Radios ------------------------------------------------------------- */
  --r-xs: 6px;
  --r-sm: 10px;
  --r-md: 14px;
  --r-lg: 20px;
  --r-xl: 28px;
  --r-full: 999px;

  /* --- Layout ------------------------------------------------------------- */
  --page-max: 1180px;
  --page-gutter: clamp(1rem, 0.4rem + 2.2vw, 2.5rem);
  /* Alto del cromo fijo. Lo consume `scroll-padding-top`, así que tiene que ser
     el alto REAL o el destino de cada salto aterriza debajo de la barra. Bajo
     900 px el cromo envuelve las pestañas a un segundo renglón y mide 88 px; el
     valor único de 52 px dejaba el encabezado de la vista tapado justo para quien
     usa el enlace «Ir al contenido», que existe sólo para navegación por teclado.
     El override va abajo, con las demás reglas responsivas. */
  --chrome-h: 52px;

  /* --- Motion ------------------------------------------------------------- */
  /* Curva de Apple para transiciones no gestuales. Lo gestual usa resortes JS. */
  --ease-out: cubic-bezier(0.32, 0.72, 0, 1);
  --ease-in-out: cubic-bezier(0.5, 0, 0.5, 1);
  --dur-instant: 100ms;
  --dur-fast: 180ms;
  --dur-base: 280ms;
  --dur-slow: 420ms;

  /* ======================================================================
     Datos: paleta categórica.
     Los pasos oscuros están elegidos para la superficie oscura; no son un
     flip automático de los claros.
     ====================================================================== */
  --series-1: #2a78d6; /* azul     — urbana / principal */
  --series-1: light-dark(#2a78d6, #3987e5);
  --series-2: #eb6834; /* naranja  — rural */
  --series-2: light-dark(#eb6834, #d95926);
  --series-3: #1baf7a; /* aqua */
  --series-3: light-dark(#1baf7a, #199e70);
  --series-4: #eda100; /* amarillo */
  --series-4: light-dark(#eda100, #c98500);
  --series-5: #e87ba4; /* magenta */
  --series-5: light-dark(#e87ba4, #d55181);
  --series-6: #008300; /* verde — invariante */
  --series-7: #4a3aa7; /* violeta */
  --series-7: light-dark(#4a3aa7, #9085e9);
  --series-8: #e34948; /* rojo */
  --series-8: light-dark(#e34948, #e66767);

  /* Rampa secuencial: un solo tono, claro→oscuro. Invariante entre modos; lo
     que cambia es qué extremo queda cerca de la superficie. */
  --seq-100: #cde2fb;
  --seq-150: #b7d3f6;
  --seq-200: #9ec5f4;
  --seq-250: #86b6ef;
  --seq-300: #6da7ec;
  --seq-350: #5598e7;
  --seq-400: #3987e5;
  --seq-450: #2a78d6;
  --seq-500: #256abf;
  --seq-550: #1c5cab;
  --seq-600: #184f95;
  --seq-650: #104281;
  --seq-700: #0d366b;

  /* Relleno de barras de magnitud: el paso de la rampa que contrasta con la
     superficie de cada modo. */
  --fill-magnitude: #2a78d6;
  --fill-magnitude: light-dark(#2a78d6, #3987e5);

  /* Divergente: naranja (encareció) ↔ azul (abarató), cero neutro gris.
     Naranja↔azul en vez de rojo↔verde: es el par cálido/frío con la mayor
     separación medida bajo protanopia y deuteranopia, y evita que un tablero
     sobre hambre se lea como una alarma roja. El signo y la flecha son el canal
     redundante, así que el color nunca carga solo el sentido. */
  --div-up: #eb6834;
  --div-up: light-dark(#eb6834, #d95926);
  --div-down: #2a78d6;
  --div-down: light-dark(#2a78d6, #3987e5);
  --div-mid: #c3c2b7;
  --div-mid: light-dark(#c3c2b7, #383835);

  /* Estado (fijo, nunca tematizado; siempre con icono + etiqueta) */
  --status-good: #0ca30c;
  --status-warning: #fab219;
  --status-serious: #ec835a;
  --status-critical: #d03b3b;

  /* --- Superficies e ink -------------------------------------------------- */
  --plane: #f9f9f7; /* plano de la página */
  --plane: light-dark(#f9f9f7, #0d0d0d);
  --surface-1: #fcfcfb; /* superficie de tarjeta / gráfica */
  --surface-1: light-dark(#fcfcfb, #1a1a19);
  --surface-2: #f3f2ee; /* superficie hundida (tracks, celdas) */
  --surface-2: light-dark(#f3f2ee, #222220);
  --surface-hover: rgba(11, 11, 11, 0.035);
  --surface-hover: light-dark(rgba(11, 11, 11, 0.035), rgba(255, 255, 255, 0.05));
  --surface-active: rgba(11, 11, 11, 0.06);
  --surface-active: light-dark(rgba(11, 11, 11, 0.06), rgba(255, 255, 255, 0.09));

  --ink-1: #0b0b0b; /* claro 18.7:1 · oscuro 17.4:1 */
  --ink-1: light-dark(#0b0b0b, #ffffff);
  --ink-2: #52514e; /* claro  7.5:1 · oscuro  9.7:1 */
  --ink-2: light-dark(#52514e, #c3c2b7);
  --ink-3: #6b6a65; /* claro  5.1:1 · oscuro  6.7:1 — atenuado pero AA */
  --ink-3: light-dark(#6b6a65, #a3a19a);
  --ink-inverse: #fcfcfb;
  --ink-inverse: light-dark(#fcfcfb, #0d0d0d);
  /* Rótulos de eje y ticks.
     Eran #898781, 3.4:1 sobre el plano claro. Se justificaba como «deliberadamente
     recesivo» con la tabla de datos de respaldo, pero eso confunde dos criterios:
     un rótulo de eje es TEXTO, y el 1.4.3 le pide 4.5:1 sin importar que exista
     otra forma de llegar al mismo dato. Un eje que no se lee obliga a abrir la
     tabla para saber de qué va la gráfica. Ahora pasa en los dos temas y sobre las
     dos superficies —plano y tarjeta— y sigue siendo el tono más tenue del
     sistema, que es lo que se buscaba. */
  --ink-chrome: #726f66; /* claro 4.8:1 · oscuro 7.5:1 */
  --ink-chrome: light-dark(#726f66, #a3a19a);

  --hairline: rgba(11, 11, 11, 0.1);
  --hairline: light-dark(rgba(11, 11, 11, 0.1), rgba(255, 255, 255, 0.1));
  --hairline-strong: rgba(11, 11, 11, 0.16);
  --hairline-strong: light-dark(rgba(11, 11, 11, 0.16), rgba(255, 255, 255, 0.18));
  --grid: #e1e0d9;
  --grid: light-dark(#e1e0d9, #2c2c2a);
  --axis: #c3c2b7;
  --axis: light-dark(#c3c2b7, #383835);

  /* Dirección del cambio. El texto del valor va en tinta; el color viaja en la
     flecha, que es un indicador gráfico (umbral 3:1) y además cambia de forma.
     `validate:palette` falla si alguien los usa como color de texto. */
  --delta-good: #2a78d6; /* abarató */
  --delta-good: light-dark(#2a78d6, #3987e5);
  --delta-bad: #eb6834; /* encareció */
  --delta-bad: light-dark(#eb6834, #d95926);

  --accent: #2a78d6;
  --accent: light-dark(#2a78d6, #3987e5);
  --accent-ink: #1c5cab;
  --accent-ink: light-dark(#1c5cab, #86b6ef);
  --accent-wash: rgba(42, 120, 214, 0.1);
  --accent-wash: light-dark(rgba(42, 120, 214, 0.1), rgba(57, 135, 229, 0.16));

  --focus-ring: #2a78d6;
  --focus-ring: light-dark(#2a78d6, #86b6ef);

  --scrim: rgba(11, 11, 11, 0.32);
  --scrim: light-dark(rgba(11, 11, 11, 0.32), rgba(0, 0, 0, 0.55));

  /* --- Materiales translúcidos ------------------------------------------- */
  --material-chrome: rgba(249, 249, 247, 0.72);
  --material-chrome: light-dark(rgba(249, 249, 247, 0.72), rgba(13, 13, 13, 0.7));
  --material-chrome-blur: saturate(180%) blur(20px);
  --material-popover: rgba(252, 252, 251, 0.82);
  --material-popover: light-dark(rgba(252, 252, 251, 0.82), rgba(26, 26, 25, 0.82));
  --material-popover-blur: saturate(180%) blur(28px);
  --material-edge: rgba(255, 255, 255, 0.6);
  --material-edge: light-dark(rgba(255, 255, 255, 0.6), rgba(255, 255, 255, 0.1));

  /* --- Sombras (superficies grandes leen más gruesas) -------------------- */
  --shadow-chip: 0 1px 2px rgba(11, 11, 11, 0.06);
  --shadow-chip: light-dark(0 1px 2px rgba(11, 11, 11, 0.06), 0 1px 2px rgba(0, 0, 0, 0.5));
  --shadow-card: 0 1px 1px rgba(11, 11, 11, 0.04), 0 3px 10px rgba(11, 11, 11, 0.045);
  --shadow-popover: 0 2px 6px rgba(11, 11, 11, 0.08), 0 14px 40px rgba(11, 11, 11, 0.14);
  --shadow-sheet: 0 -2px 8px rgba(11, 11, 11, 0.06), 0 -20px 60px rgba(11, 11, 11, 0.18);
}

/*
   Las sombras son el único token que sigue duplicado: llevan comas internas y
   `light-dark()` separa sus argumentos con coma, así que una lista de sombras no
   cabe dentro de la función. Son cuatro tokens, no cuarenta, y `--shadow-chip`
   sí cupo. Si esta lista crece, conviene pasarla a tokens de componente.
*/
@media (prefers-color-scheme: dark) {
  :root:where(:not([data-theme='light'])) {
    --shadow-card: 0 1px 1px rgba(0, 0, 0, 0.4), 0 3px 12px rgba(0, 0, 0, 0.45);
    --shadow-popover: 0 2px 8px rgba(0, 0, 0, 0.5), 0 16px 44px rgba(0, 0, 0, 0.6);
    --shadow-sheet: 0 -2px 10px rgba(0, 0, 0, 0.5), 0 -24px 64px rgba(0, 0, 0, 0.7);
  }
}

:root[data-theme='dark'] {
  --shadow-card: 0 1px 1px rgba(0, 0, 0, 0.4), 0 3px 12px rgba(0, 0, 0, 0.45);
  --shadow-popover: 0 2px 8px rgba(0, 0, 0, 0.5), 0 16px 44px rgba(0, 0, 0, 0.6);
  --shadow-sheet: 0 -2px 10px rgba(0, 0, 0, 0.5), 0 -24px 64px rgba(0, 0, 0, 0.7);
}

/* --- El toggle del usuario gana en los dos sentidos ---------------------- */

:root[data-theme='dark'] {
  color-scheme: dark;
}

:root[data-theme='light'] {
  color-scheme: light;
}

/* --- Preferencias de accesibilidad ------------------------------------- */

/* Menos transparencia: los materiales se vuelven sólidos, sin blur. */
@media (prefers-reduced-transparency: reduce) {
  :root {
    --material-chrome: light-dark(#f9f9f7, #0d0d0d);
    --material-chrome-blur: none;
    --material-popover: light-dark(#fcfcfb, #1a1a19);
    --material-popover-blur: none;
  }
}

/* Más contraste: bordes definidos, ink más fuerte. */
@media (prefers-contrast: more) {
  :root {
    --ink-2: light-dark(#2f2e2c, #e8e7de);
    --ink-3: light-dark(#4a4945, #d0cec4);
    --ink-chrome: light-dark(#5c5b56, #b8b6ac);
    --hairline: light-dark(rgba(11, 11, 11, 0.28), rgba(255, 255, 255, 0.32));
    --hairline-strong: light-dark(rgba(11, 11, 11, 0.45), rgba(255, 255, 255, 0.5));
    --grid: light-dark(#c9c8c0, #464643);
    --axis: light-dark(#7d7b74, #6c6b65);
    --material-chrome: light-dark(#f9f9f7, #0d0d0d);
    --material-popover: light-dark(#fcfcfb, #1a1a19);
    --material-chrome-blur: none;
    --material-popover-blur: none;
  }
}

/* --- Alto real del cromo por ancho --------------------------------------
   Espeja los cortes de components/chrome.css: bajo 900 px el cromo pasa a dos
   renglones (las pestañas bajan a su propia fila) y bajo 560 px además se oculta
   el chip de estado. Medido en el navegador: 88 px en los dos casos.
   ---------------------------------------------------------------------- */
@media (max-width: 900px) {
  :root {
    --chrome-h: 88px;
  }
}
